
import { useState, useEffect } from 'react';

interface NavbarProps {
  cartCount?: number;
}

export default function Navbar({ cartCount = 0 }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToCheckout = () => {
    window.REACT_APP_NAVIGATE('/checkout');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/95 shadow-lg shadow-red-900/50 backdrop-blur-sm' : 'bg-transparent'
      }`}
      style={{
        boxShadow: scrolled 
          ? '0 4px 30px rgba(139, 0, 0, 0.4), 0 0 60px rgba(139, 0, 0, 0.2), inset 0 -1px 0 rgba(139, 0, 0, 0.3)' 
          : 'none'
      }}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <div className="w-32 h-32 flex items-center justify-center">
              <img 
                src="https://static.readdy.ai/image/4abb71d54c7d60621cfc0b2fab4f7970/3b1e60a7c97fafd8931cbea82c7dfd99.png" 
                alt="Nightmare Logo" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-12">
            <button
              onClick={() => scrollToSection('bio')}
              className="text-gray-400 hover:text-white transition-colors duration-300 uppercase tracking-wider text-sm font-medium cursor-pointer"
            >
              Bio
            </button>
            <button
              onClick={() => scrollToSection('stats')}
              className="text-gray-400 hover:text-white transition-colors duration-300 uppercase tracking-wider text-sm font-medium cursor-pointer"
            >
              Stats
            </button>
            <button
              onClick={() => scrollToSection('news')}
              className="text-gray-400 hover:text-white transition-colors duration-300 uppercase tracking-wider text-sm font-medium cursor-pointer"
            >
              News
            </button>
            <button
              onClick={() => scrollToSection('merchandise')}
              className="text-gray-400 hover:text-white transition-colors duration-300 uppercase tracking-wider text-sm font-medium cursor-pointer"
            >
              Shop
            </button>
            <button
              onClick={() => scrollToSection('tips')}
              className="text-gray-400 hover:text-white transition-colors duration-300 uppercase tracking-wider text-sm font-medium cursor-pointer"
            >
              Tips
            </button>
          </div>

          {/* Right Side - Cart Icon */}
          <div className="flex items-center space-x-6">
            <button
              onClick={goToCheckout}
              className="relative w-10 h-10 flex items-center justify-center text-gray-400 hover:text-[#8B0000] transition-colors cursor-pointer"
            >
              <i className="ri-shopping-cart-2-line text-xl"></i>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center bg-[#8B0000] text-white text-[10px] font-bold rounded-full px-1 animate-cart-bump">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes cartBump {
          0% { transform: scale(1); }
          50% { transform: scale(1.3); }
          100% { transform: scale(1); }
        }
        .animate-cart-bump {
          animation: cartBump 0.3s ease-out;
        }
      `}} />
    </nav>
  );
}
